import { useEffect, useState } from 'react';
import { ERROR_PREFIXES } from 'src/common/constants';
import { useSessionManagement } from 'src/services/auth/session/hooks/useSessionManagement';
import { useMigrateToZillowState } from 'src/experiences/MigrateToZillow/hooks/useMigrateToZillowState';
import { DatadogRUMService } from 'src/services/datadog/DatadogRUMService';
import { createDatadogError } from 'src/services/datadog/utils/createDatadogError';
import { MaybeString } from 'src/common/types';
import { useSessionStoragePersistedState } from 'src/services/storage/hooks/useSessionStoragePersistedState';
import { SessionStorageKeys } from 'src/services/storage/constants';
import { SessionStatus } from '../../../constants';

export const WithAuthError = createDatadogError('WithAuth', ERROR_PREFIXES.APPLICATION_SESSION);
export const useDecideIfLoginIsRequired = (forceAuth: boolean, emailLoginHint?: MaybeString) => {
    const { isRedirectDecisionMade } = useMigrateToZillowState();
    const [hasMadeAuthenticationDecision, setHasMadeAuthenticationDecision] = useState<boolean>(forceAuth);
    const { startSession, status, session } = useSessionManagement();
    const [error, setError] = useState<unknown | null>(null);
    const [_, setLoginHintDisplayed] = useSessionStoragePersistedState(SessionStorageKeys.LOGIN_HINT_DISPLAYED, false);
    const [__, setPreferredLoginEmail] = useSessionStoragePersistedState(SessionStorageKeys.PREFERRED_LOGIN_EMAIL, '');

    const shouldRedirectToLogin = status === SessionStatus.UNAUTHENTICATED && isRedirectDecisionMade;
    useEffect(() => {
        const maybeAuthenticate = async () => {
            try {
                // we set this regardless of if she sees the hint because we need to know if Beth activated with
                // a different email than her comms email
                if (emailLoginHint) {
                    setPreferredLoginEmail(emailLoginHint);
                }
                if (shouldRedirectToLogin) {
                    if (emailLoginHint) {
                        // we use this in activate bounce to determine if she saw login  hint
                        // we have to use session storage because Beth is redirected to auth0 sign in first
                        setLoginHintDisplayed(true);
                    }
                    startSession(
                        'withAuth determined login is necessary',
                        {},
                        { ...(emailLoginHint && { login_hint: emailLoginHint }) }
                    );
                }
            } catch (err) {
                const error = new WithAuthError(`An error occurred while logging the user in`);
                error.stack = (err as Error).stack;
                await DatadogRUMService.Client.getInstance().addError(error, {
                    error: (err as Error).message,
                });
                setError(error);
            }
            setHasMadeAuthenticationDecision(false);
        };

        maybeAuthenticate();
    }, [shouldRedirectToLogin, startSession, status, emailLoginHint, setLoginHintDisplayed, setPreferredLoginEmail]);

    const isClientSideDecisionPending =
        hasMadeAuthenticationDecision || status === SessionStatus.LOADING || shouldRedirectToLogin;

    const isAuthenticationDecisionPending = isClientSideDecisionPending;
    return { error, session, status, isAuthenticationDecisionPending };
};
